import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { FiX, FiSave, FiEye, FiTrash2, FiPlus, FiAlertTriangle, FiRotateCcw } from "react-icons/fi";
import { supabase } from "../../lib/supabaseClient";
import axios from "axios";
import { calculerProrataSurExercice } from "../../utils/prorataExercice";

const EDITABLE_FIELDS = [
  { key: "periode", label: "Période", type: "text", required: true },
  { key: "assiette_kwh", label: "Assiette (kWh)", type: "number" },
  { key: "prix_unitaire_ht", label: "Prix unitaire HT", type: "number" },
  { key: "montant_total_cspe", label: "Montant total TICGN", type: "number" },
  { key: "cspe_line_found", label: "TICGN trouvée", type: "boolean" },
];

const isEmptyOverrideObject = (obj) => !obj || Object.keys(obj).length === 0;

const toNullableNumber = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const formatPeriodeInput = (raw) => {
  const digits = raw.replace(/\D/g, '');
  let result = '';
  for (let i = 0; i < digits.length && i < 16; i++) {
    if (i === 2 || i === 4 || i === 10 || i === 12) result += '/';
    if (i === 8) result += ' - ';
    result += digits[i];
  }
  return result;
};

const parsePeriodeStart = (periode) => {
  if (!periode) return new Date(9999, 0, 1);
  const m = String(periode).match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (!m) return new Date(9999, 0, 1);
  return new Date(parseInt(m[3]), parseInt(m[2]) - 1, parseInt(m[1]));
};

export default function TicgnInvoiceEditorModal({
  open,
  onClose,
  accountId,
  pce,           // { id, site_name }
  year,
  adminUserId,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [ocrRows, setOcrRows] = useState([]);
  const [manualRows, setManualRows] = useState([]);
  const [manualDeleteIds, setManualDeleteIds] = useState(() => new Set());

  // Bornes de l'exercice fiscal (pour proratiser la bande recap)
  const [exerciceBounds, setExerciceBounds] = useState(null); // { start, end } ISO strings

  const title = useMemo(() => {
    const name = pce?.site_name || "PCE";
    return `Détail factures gaz – ${name} (${year})`;
  }, [pce?.site_name, year]);

  const getEffective = (row, key) => {
    const ov = row?.overrides?.[key];
    if (ov !== undefined && ov !== null && ov !== "") return ov;
    return row?.base?.[key] ?? "";
  };

  const setOcrOverrideValue = (index, key, value) => {
    setOcrRows((prev) => {
      const copy = [...prev];
      const row = { ...copy[index], overrides: { ...(copy[index].overrides || {}) } };
      if (value === "" || value === null || value === undefined) {
        delete row.overrides[key];
      } else {
        row.overrides[key] = value;
      }
      copy[index] = row;
      return copy;
    });
  };

  const clearOcrOverrides = (index) => {
    setOcrRows((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], overrides: {} };
      return copy;
    });
  };

  const addManualLine = () => {
    const tempId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? `temp_${crypto.randomUUID()}`
        : `temp_${Date.now()}_${Math.random().toString(16).slice(2)}`;

    setManualRows((prev) => [
      ...prev,
      {
        id: tempId,
        account_id: accountId,
        pce_id: pce?.id,
        year,
        periode: "",
        assiette_kwh: "",
        prix_unitaire_ht: "",
        montant_total_cspe: "",
        cspe_line_found: true,
        reason: "",
      },
    ]);
  };

  const setManualValue = (index, key, value) => {
    setManualRows((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [key]: value };
      return copy;
    });
  };

  const deleteManualRow = (index) => {
    setManualRows((prev) => {
      const row = prev[index];
      const isTemp = String(row?.id || "").startsWith("temp_");
      if (!isTemp && row?.id) {
        setManualDeleteIds((s) => new Set([...Array.from(s), row.id]));
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleDeleteOcrRow = async (row) => {
    if (!window.confirm(`Supprimer définitivement la ligne OCR "${row.base.periode || 'sans période'}" ?\nCette action est irréversible.`)) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await axios.delete(`/api/ticgn/documents/extraction/${row.base.id}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      setOcrRows(prev => prev.filter(r => r.base.id !== row.base.id));
      toast.success("Ligne OCR supprimée.");
    } catch (e) {
      toast.error(`Erreur suppression : ${e.response?.data?.message || e.message}`);
    }
  };

  // Load data when modal opens
  useEffect(() => {
    if (!open) return;
    if (!accountId || !pce?.id || !year) return;

    const load = async () => {
      setLoading(true);
      setManualDeleteIds(new Set());

      try {
        // 1) OCR base
        const { data: base, error: baseErr } = await supabase
          .from("TICGN_Extraction_Results")
          .select("*")
          .eq("account_id", accountId)
          .eq("pce_id", pce.id)
          .eq("year", year)
          .order("created_at", { ascending: true });

        if (baseErr) throw baseErr;

        const baseIds = (base || []).map((r) => r.id);

        // 2) Overrides
        let overridesById = new Map();
        if (baseIds.length > 0) {
          const { data: ov, error: ovErr } = await supabase
            .from("ticgn_extraction_overrides")
            .select("extraction_result_id, overrides")
            .in("extraction_result_id", baseIds);

          if (ovErr) throw ovErr;
          overridesById = new Map((ov || []).map((o) => [o.extraction_result_id, o.overrides || {}]));
        }

        // 3) Manual lines
        const { data: manual, error: manualErr } = await supabase
          .from("ticgn_manual_lines")
          .select("*")
          .eq("account_id", accountId)
          .eq("pce_id", pce.id)
          .eq("year", year)
          .order("created_at", { ascending: true });

        if (manualErr) throw manualErr;

        setOcrRows(
          (base || [])
            .map((b) => ({
              base: b,
              overrides: overridesById.get(b.id) || {},
              expanded: false,
            }))
            .sort((a, b) => parsePeriodeStart(a.base.periode) - parsePeriodeStart(b.base.periode))
        );

        setManualRows(manual || []);

        // 4) Bornes exercice pour l'annee fiscale courante
        // Convention article 1586 quinquies CGI : annee_fiscale = année calendaire de clôture.
        const { data: fiscalRow } = await supabase
          .from("fiscal_declarations_data")
          .select("exercice_start_date, exercice_end_date")
          .eq("account_id", accountId)
          .gte("exercice_end_date", `${year}-01-01`)
          .lte("exercice_end_date", `${year}-12-31`)
          .order("exercice_end_date", { ascending: false })
          .limit(1)
          .maybeSingle();

        setExerciceBounds(
          fiscalRow?.exercice_start_date && fiscalRow?.exercice_end_date
            ? { start: fiscalRow.exercice_start_date, end: fiscalRow.exercice_end_date }
            : null
        );
      } catch (e) {
        console.error(e);
        toast.error("Impossible de charger les données TICGN (OCR/overrides/manuelles).");
        setOcrRows([]);
        setManualRows([]);
        setExerciceBounds(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, accountId, pce?.id, year]);

  const totals = useMemo(() => {
    // Totaux proratises jour/jour sur les bornes de l'exercice fiscal
    // (aligne avec le CERFA ERA-G et le CERFA TIC-VA-GC cote backend)
    const bounds = exerciceBounds
      ? { dateDebutExercice: exerciceBounds.start, dateFinExercice: exerciceBounds.end }
      : null;

    const all = [];
    for (const r of ocrRows) {
      const kwh = toNullableNumber(getEffective(r, "assiette_kwh"));
      const total = toNullableNumber(getEffective(r, "montant_total_cspe"));
      const periode = getEffective(r, "periode");
      const prorata = bounds ? calculerProrataSurExercice({ periode, ...bounds }) : 1;
      all.push({ kwh, total, prorata, counted: !bounds || prorata > 0 });
    }
    for (const r of manualRows) {
      const kwh = toNullableNumber(r.assiette_kwh);
      const total = toNullableNumber(r.montant_total_cspe);
      const prorata = bounds ? calculerProrataSurExercice({ periode: r.periode, ...bounds }) : 1;
      all.push({ kwh, total, prorata, counted: !bounds || prorata > 0 });
    }
    const sumKwh = all.reduce((acc, x) => acc + (x.kwh || 0) * x.prorata, 0);
    const sumMwh = sumKwh / 1000;
    const sumTotal = all.reduce((acc, x) => acc + (x.total || 0) * x.prorata, 0);
    const count = all.filter((x) => x.counted).length;
    return { count, sumKwh, sumMwh, sumTotal, hasBounds: !!bounds };
  }, [ocrRows, manualRows, exerciceBounds]);

  const saveAll = async () => {
    if (!accountId || !pce?.id || !year) return;

    const invalidManual = manualRows.find((r) => !r.periode || !String(r.periode).trim());
    if (invalidManual) {
      toast.error("Une ligne manuelle est incomplète : la période est obligatoire.");
      return;
    }

    const invalidOcr = ocrRows.find((r) => {
      const effectiveCspe =
        r.overrides?.cspe_line_found !== undefined
          ? !!r.overrides.cspe_line_found
          : !!r.base.cspe_line_found;
      const effectivePeriode = r.overrides?.periode || r.base.periode;
      return effectiveCspe === true && !effectivePeriode;
    });
    if (invalidOcr) {
      toast.error(
        "Une ligne OCR est cochée 'TICGN trouvée' mais sa période est vide. Renseignez la période ou décochez la case.",
        { duration: 5000 }
      );
      return;
    }

    const periodeRegex = /^\d{2}\/\d{2}\/\d{4} - \d{2}\/\d{2}\/\d{4}$/;
    const malformedPeriode = [
      ...ocrRows.filter((r) => {
        const p = r.overrides?.periode || r.base.periode;
        return p && !periodeRegex.test(p);
      }),
      ...manualRows.filter((r) => r.periode && !periodeRegex.test(r.periode)),
    ];
    if (malformedPeriode.length) {
      toast.error(
        `Format de période invalide (${malformedPeriode.length} ligne(s)). Attendu : JJ/MM/AAAA - JJ/MM/AAAA`,
        { duration: 5000 }
      );
      return;
    }

    const parseDatePeriode = (str) => {
      const m = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (!m) return null;
      return new Date(parseInt(m[3]), parseInt(m[2]) - 1, parseInt(m[1]));
    };
    const incoherentPeriode = [
      ...ocrRows.filter((r) => {
        const p = r.overrides?.periode || r.base.periode;
        if (!p || !p.includes(' - ')) return false;
        const [dStr, fStr] = p.split(' - ').map(s => s.trim());
        const d = parseDatePeriode(dStr), f = parseDatePeriode(fStr);
        return d && f && d > f;
      }),
      ...manualRows.filter((r) => {
        if (!r.periode || !r.periode.includes(' - ')) return false;
        const [dStr, fStr] = r.periode.split(' - ').map(s => s.trim());
        const d = parseDatePeriode(dStr), f = parseDatePeriode(fStr);
        return d && f && d > f;
      }),
    ];
    if (incoherentPeriode.length) {
      toast.error(
        `${incoherentPeriode.length} période(s) incohérente(s) : la date de début est après la date de fin.`,
        { duration: 5000 }
      );
      return;
    }

    setSaving(true);
    const toastId = toast.loading("Enregistrement des corrections…");

    try {
      // A) Overrides
      for (const r of ocrRows) {
        const extractionId = r.base.id;
        const overrides = r.overrides || {};
        const hasOverride = !isEmptyOverrideObject(overrides);

        if (!hasOverride) {
          const { error: delErr } = await supabase
            .from("ticgn_extraction_overrides")
            .delete()
            .eq("extraction_result_id", extractionId);
          if (delErr) throw delErr;
          continue;
        }

        const payload = {
          extraction_result_id: extractionId,
          account_id: accountId,
          pce_id: pce.id,
          year,
          overrides,
          updated_by: adminUserId || null,
        };

        const { error: upErr } = await supabase
          .from("ticgn_extraction_overrides")
          .upsert(payload, { onConflict: "extraction_result_id" });
        if (upErr) throw upErr;
      }

      // B) Suppression lignes manuelles marquees
      for (const id of Array.from(manualDeleteIds)) {
        const { error: delErr } = await supabase
          .from("ticgn_manual_lines")
          .delete()
          .eq("id", id)
          .eq("account_id", accountId);
        if (delErr) throw delErr;
      }

      // C) Insert / Update lignes manuelles
      for (const r of manualRows) {
        const isTemp = String(r.id).startsWith("temp_");

        const payload = {
          account_id: accountId,
          pce_id: pce.id,
          year,
          periode: String(r.periode || "").trim(),
          assiette_kwh: toNullableNumber(r.assiette_kwh),
          prix_unitaire_ht: toNullableNumber(r.prix_unitaire_ht),
          montant_total_cspe: toNullableNumber(r.montant_total_cspe),
          cspe_line_found: !!r.cspe_line_found,
          fields: r.fields || {},
          reason: r.reason || null,
          updated_by: adminUserId || null,
        };

        if (isTemp) {
          payload.created_by = adminUserId || null;
          const { error: insErr } = await supabase.from("ticgn_manual_lines").insert(payload);
          if (insErr) throw insErr;
        } else {
          const { error: updErr } = await supabase
            .from("ticgn_manual_lines")
            .update(payload)
            .eq("id", r.id)
            .eq("account_id", accountId);
          if (updErr) throw updErr;
        }
      }

      toast.success("Corrections enregistrées (OVERRIDE + lignes manuelles TICGN).", { id: toastId });
      onClose?.(true);
    } catch (e) {
      console.error(e);
      toast.error(`Erreur sauvegarde: ${e.message || "inconnue"}`, { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-start sm:items-center justify-center bg-black/40 p-2 sm:p-4">
      <div className="w-full max-w-4xl bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b bg-teal-50">
          <div className="min-w-0">
            <div className="text-[12px] font-bold text-slate-800 truncate">{title}</div>
            <div className="text-[10px] text-slate-500 truncate">
              <span className="hidden sm:inline">Account: </span>
              <span className="font-mono">{accountId}</span>
              <span className="mx-1">•</span>
              <span className="hidden sm:inline">PCE: </span>
              <span className="font-mono">{pce?.id}</span>
            </div>
          </div>
          <button
            onClick={() => onClose?.(false)}
            className="p-1.5 rounded hover:bg-slate-200 text-slate-600"
            title="Fermer"
          >
            <FiX className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-3 space-y-3 max-h-[calc(100vh-120px)] overflow-y-auto">
          {/* Resume */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="p-2 rounded border bg-white">
              <div className="text-[10px] text-slate-500">Lignes</div>
              <div className="text-[13px] font-bold text-slate-800">{totals.count}</div>
            </div>
            <div className="p-2 rounded border bg-white">
              <div className="text-[10px] text-slate-500">
                Total kWh {totals.hasBounds
                  ? <span className="text-emerald-600">(prorata)</span>
                  : <span className="text-amber-600">(brut)</span>}
              </div>
              <div className="text-[13px] font-bold text-slate-800">{totals.sumKwh.toFixed(0)}</div>
            </div>
            <div className="p-2 rounded border bg-white">
              <div className="text-[10px] text-slate-500">Total MWh</div>
              <div className="text-[13px] font-bold text-slate-800">{totals.sumMwh.toFixed(3)}</div>
            </div>
            <div className="p-2 rounded border bg-white">
              <div className="text-[10px] text-slate-500">Total TICGN</div>
              <div className="text-[13px] font-bold text-slate-800">{totals.sumTotal.toFixed(2)}</div>
            </div>
          </div>

          {/* OCR section */}
          <div className="bg-white border rounded">
            <div className="px-2.5 py-2 border-b bg-teal-50 flex items-start sm:items-center justify-between gap-2">
              <div className="text-[11px] font-semibold text-slate-700">
                Extractions OCR + Overrides
              </div>
              <div className="hidden sm:block text-[10px] text-slate-500">
                Saisi = override (JSONB)
              </div>
            </div>

            {loading ? (
              <div className="p-2.5 text-[11px] text-slate-600">Chargement…</div>
            ) : ocrRows.length === 0 ? (
              <div className="p-2.5 flex items-center gap-2 text-[11px] text-amber-700 bg-amber-50">
                <FiAlertTriangle className="shrink-0" />
                <span>
                  Aucune extraction OCR pour {year}. Ajoute une ligne manuelle ci-dessous.
                </span>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-white">
                    <tr className="text-[10px] text-slate-500 uppercase">
                      <th className="px-2 py-1.5 text-left">Période</th>
                      <th className="px-2 py-1.5 text-left">kWh</th>
                      <th className="px-2 py-1.5 text-left">PU</th>
                      <th className="px-2 py-1.5 text-left">TICGN</th>
                      <th className="px-2 py-1.5 text-left">OK</th>
                      <th className="px-2 py-1.5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {ocrRows.map((row, idx) => {
                      const base = row.base;
                      const hasOverride = !isEmptyOverrideObject(row.overrides);
                      const inputBase = "h-7 px-2 py-0.5 border rounded text-[11px] leading-none";

                      return (
                        <React.Fragment key={base.id}>
                          <tr className="hover:bg-slate-50">
                            <td className="px-2 py-1.5 align-top">
                              <input
                                className={`h-7 px-2 py-0.5 border rounded text-[11px] leading-none w-44 ${
                                  !base.periode ? 'border-orange-300 bg-orange-50' : ''
                                }`}
                                value={row.overrides?.periode ?? ""}
                                placeholder={base.periode || "JJ/MM/AAAA - JJ/MM/AAAA"}
                                onChange={(e) => setOcrOverrideValue(idx, "periode", formatPeriodeInput(e.target.value))}
                              />
                              {hasOverride && (
                                <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[9px] font-bold">
                                  OV
                                </span>
                              )}
                              <div className="text-[9px] text-slate-400 mt-0.5">
                                OCR: {base.periode || (
                                  <span className="text-orange-500 font-semibold">à renseigner</span>
                                )}
                              </div>
                            </td>
                            <td className="px-2 py-1.5">
                              <input className={`${inputBase} w-20`} value={row.overrides?.assiette_kwh ?? ""} placeholder={String(base.assiette_kwh ?? "")} onChange={(e) => setOcrOverrideValue(idx, "assiette_kwh", e.target.value)} />
                            </td>
                            <td className="px-2 py-1.5">
                              <input className={`${inputBase} w-16`} value={row.overrides?.prix_unitaire_ht ?? ""} placeholder={String(base.prix_unitaire_ht ?? "")} onChange={(e) => setOcrOverrideValue(idx, "prix_unitaire_ht", e.target.value)} />
                            </td>
                            <td className="px-2 py-1.5">
                              <input className={`${inputBase} w-20`} value={row.overrides?.montant_total_cspe ?? ""} placeholder={String(base.montant_total_cspe ?? "")} onChange={(e) => setOcrOverrideValue(idx, "montant_total_cspe", e.target.value)} />
                            </td>
                            <td className="px-2 py-1.5">
                              <label className="inline-flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="scale-90"
                                  checked={
                                    row.overrides?.cspe_line_found !== undefined
                                      ? !!row.overrides.cspe_line_found
                                      : !!base.cspe_line_found
                                  }
                                  onChange={(e) => setOcrOverrideValue(idx, "cspe_line_found", e.target.checked)}
                                />
                                <span className="hidden sm:inline text-[11px] text-slate-700">
                                  {(row.overrides?.cspe_line_found !== undefined ? !!row.overrides.cspe_line_found : !!base.cspe_line_found) ? "Oui" : "Non"}
                                </span>
                              </label>
                            </td>
                            <td className="px-2 py-1.5 text-right">
                              <div className="inline-flex items-center gap-1.5">
                                <button className="p-1.5 rounded hover:bg-slate-100 text-slate-700" title="Voir extrait OCR" onClick={() => { setOcrRows((prev) => { const copy = [...prev]; copy[idx] = { ...copy[idx], expanded: !copy[idx].expanded }; return copy; }); }}>
                                  <FiEye className="h-4 w-4" />
                                </button>
                                {hasOverride && (
                                  <button className="p-1.5 rounded hover:bg-amber-50 text-amber-600" title="Annuler mes corrections" onClick={() => { clearOcrOverrides(idx); toast('Corrections annulées pour cette ligne.', { icon: '↺' }); }}>
                                    <FiRotateCcw className="h-3.5 w-3.5" />
                                  </button>
                                )}
                                <button className="p-1.5 rounded hover:bg-red-100 text-red-800" title="Supprimer définitivement cette ligne OCR" onClick={() => handleDeleteOcrRow(row)}>
                                  <FiTrash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                          {row.expanded && (
                            <tr className="bg-slate-50">
                              <td colSpan={6} className="px-2 py-2">
                                <div className="text-[11px] font-semibold text-slate-700 mb-1">Extrait détecté</div>
                                <pre className="text-[10px] whitespace-pre-wrap bg-white border rounded p-2 max-h-32 overflow-auto">
                                  {base.found_line_text || "—"}
                                </pre>
                                <div className="mt-2 text-[10px] text-slate-600 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                  <div>provider_used: <span className="font-mono">{base.provider_used || "—"}</span></div>
                                  <div>extraction_status: <span className="font-mono">{base.extraction_status || "—"}</span></div>
                                  <div>meter_reference: <span className="font-mono">{base.meter_reference || "—"}</span></div>
                                  <div>raw_text_length: <span className="font-mono">{base.raw_text_length ?? "—"}</span></div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Manual lines section */}
          <div className="bg-white border rounded">
            <div className="px-2.5 py-2 border-b bg-teal-50 flex items-center justify-between">
              <div className="text-[11px] font-semibold text-slate-700">
                Lignes manuelles
              </div>
              <button
                onClick={addManualLine}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded bg-teal-600 text-white hover:bg-teal-700"
              >
                <FiPlus className="h-3 w-3" />
                Ajouter
              </button>
            </div>

            {manualRows.length === 0 ? (
              <div className="p-2.5 text-[11px] text-slate-600">
                Aucune ligne manuelle.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-white">
                    <tr className="text-[10px] text-slate-500 uppercase">
                      <th className="px-2 py-1.5 text-left">Période *</th>
                      <th className="px-2 py-1.5 text-left">kWh</th>
                      <th className="px-2 py-1.5 text-left">PU</th>
                      <th className="px-2 py-1.5 text-left">TICGN</th>
                      <th className="px-2 py-1.5 text-left">OK</th>
                      <th className="px-2 py-1.5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {manualRows.map((r, idx) => {
                      const inputBase = "h-7 px-2 py-0.5 border rounded text-[11px] leading-none";
                      return (
                        <tr key={r.id} className="hover:bg-slate-50">
                          <td className="px-2 py-1.5">
                            <input className={`${inputBase} w-52`} value={r.periode || ""} placeholder="JJ/MM/AAAA - JJ/MM/AAAA" onChange={(e) => setManualValue(idx, "periode", formatPeriodeInput(e.target.value))} />
                          </td>
                          <td className="px-2 py-1.5">
                            <input className={`${inputBase} w-20`} value={r.assiette_kwh ?? ""} onChange={(e) => setManualValue(idx, "assiette_kwh", e.target.value)} />
                          </td>
                          <td className="px-2 py-1.5">
                            <input className={`${inputBase} w-16`} value={r.prix_unitaire_ht ?? ""} onChange={(e) => setManualValue(idx, "prix_unitaire_ht", e.target.value)} />
                          </td>
                          <td className="px-2 py-1.5">
                            <input className={`${inputBase} w-20`} value={r.montant_total_cspe ?? ""} onChange={(e) => setManualValue(idx, "montant_total_cspe", e.target.value)} />
                          </td>
                          <td className="px-2 py-1.5">
                            <input type="checkbox" className="scale-90" checked={!!r.cspe_line_found} onChange={(e) => setManualValue(idx, "cspe_line_found", e.target.checked)} />
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <button className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Supprimer" onClick={() => deleteManualRow(idx)}>
                              <FiTrash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="px-2.5 py-2 text-[10px] text-slate-500">
                  * La période est obligatoire.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-3 py-2 border-t bg-white flex items-center justify-between gap-2">
          <div className="hidden sm:block text-[10px] text-slate-500">
            OCR brut inchangé • Overrides JSONB • Lignes manuelles TICGN
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <button onClick={() => onClose?.(false)} className="px-2.5 py-1.5 text-[11px] rounded border border-slate-200 hover:bg-slate-50">
              Fermer
            </button>
            <button
              onClick={saveAll}
              disabled={saving || loading}
              className={`px-2.5 py-1.5 text-[11px] rounded text-white inline-flex items-center gap-2 ${
                saving || loading ? "bg-slate-400 cursor-not-allowed" : "bg-teal-600 hover:bg-teal-700"
              }`}
            >
              <FiSave className="h-4 w-4" />
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
